import type { Theme, QuestionPaper, Question, QuizAttempt, QuizAttemptResponse } from '../types/quiz';
import api from '../api';

export const QuizService = {
    // Theme Management
    async getThemes(token: string): Promise<Theme[]> {
        const response = await api.get('/quiz/themes', {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    },

    async createTheme(theme: Partial<Theme>, token: string): Promise<Theme> {
        const response = await api.post('/quiz/themes', theme, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    },

    async updateTheme(themeId: number, theme: Partial<Theme>, token: string): Promise<Theme> {
        const response = await api.put(`/quiz/themes/${themeId}`, theme, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    },

    async deleteTheme(themeId: number, token: string): Promise<void> {
        await api.delete(`/quiz/themes/${themeId}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
    },

    // Question Paper Management
    async getPapers(themeId: number, token: string): Promise<QuestionPaper[]> {
        const response = await api.get(`/quiz/themes/${themeId}/papers`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    },

    async getQuestionPaper(paperId: number, token: string): Promise<QuestionPaper> {
        const response = await api.get(`/quiz/papers/${paperId}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    },

    async createQuestionPaper(paper: Partial<QuestionPaper>, token: string): Promise<QuestionPaper> {
        const response = await api.post(`/quiz/themes/${paper.theme_id}/papers`, paper, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    },

    async updateQuestionPaper(paperId: number, paper: Partial<QuestionPaper>, token: string): Promise<QuestionPaper> {
        const response = await api.put(`/quiz/papers/${paperId}`, paper, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    },

    async deleteQuestionPaper(paperId: number, token: string): Promise<void> {
        await api.delete(`/quiz/papers/${paperId}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
    },

    // Question Management
    async getQuestions(paperId: number, token: string): Promise<Question[]> {
        const response = await api.get(`/quiz/papers/${paperId}/questions`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    },

    async createQuestion(paperId: number, question: Partial<Question>, token: string): Promise<Question> {
        const response = await api.post(`/quiz/papers/${paperId}/questions`, question, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    },

    async updateQuestion(questionId: number, question: Partial<Question>, token: string): Promise<Question> {
        const response = await api.put(`/quiz/questions/${questionId}`, question, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    },

    async deleteQuestion(questionId: number, token: string): Promise<void> {
        await api.delete(`/quiz/questions/${questionId}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
    },

    async reorderQuestions(paperId: number, questionIds: number[], token: string): Promise<Question[]> {
        const response = await api.put(`/quiz/papers/${paperId}/questions/reorder`, 
            { question_ids: questionIds },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data;
    },

    async uploadCertificate(paperId: number, file: File, token: string): Promise<string> {
        const formData = new FormData();
        formData.append('certificate', file);

        const response = await api.post(`/quiz/papers/${paperId}/certificate`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${token}`
            }
        });
        return response.data.certificate_path;
    },

    // Student Quiz Attempt Endpoints
    async startQuiz(paperId: number, token: string): Promise<{ attempt_id: number; questions: Question[] }> {
        const response = await api.post(`/student/quizzes/${paperId}/start`, {}, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    },

    async getQuizAttempt(attemptId: number, token: string): Promise<QuizAttemptResponse> {
        const response = await api.get(`/student/quiz-attempts/${attemptId}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    },

    async submitQuizAttempt(attemptId: number, answers: { [key: number]: number }, token: string): Promise<{ score: number; grade: string }> {
        const response = await api.post(`/student/quiz-attempts/${attemptId}/submit`, 
            { answers },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data;
    },

    async getQuizAttempts(token: string): Promise<QuizAttempt[]> {
        const response = await api.get('/student/quiz-attempts', {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    },

    async downloadCertificate(attemptId: number, token: string): Promise<Blob> {
        const response = await api.get(`/quiz/attempts/${attemptId}/certificate`, {
            responseType: 'blob',
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    },

    async emailCertificate(attemptId: number, token: string): Promise<void> {
        const lang = localStorage.getItem('i18nextLng') || 'en';
        await api.post(`/quiz/attempts/${attemptId}/email-certificate`, { lang }, {
            headers: { Authorization: `Bearer ${token}` }
        });
    },

    async getDashboardStats(token: string): Promise<any> {
        const response = await api.get('/student/dashboard/stats', {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    }
};
